<script setup lang="ts">
import { usePreferredReducedMotion } from '@vueuse/core'
import { motion } from 'motion-v'
import type { CSSProperties, HTMLAttributes } from 'vue'
import { computed, useSlots } from 'vue'
import { cn } from '../utils'

export interface TextShimmerProps {
  as?: keyof HTMLElementTagNameMap
  class?: HTMLAttributes['class']
  duration?: number
  spread?: number
}

const props = withDefaults(defineProps<TextShimmerProps>(), {
  as: 'p',
  duration: 2,
  spread: 2,
})

const slots = useSlots()
const reducedMotion = usePreferredReducedMotion()

const textContent = computed(() => {
  const defaultSlot = slots.default?.()
  if (!defaultSlot || defaultSlot.length === 0) return ''

  return defaultSlot
    .map((vnode) => {
      if (typeof vnode.children === 'string') {
        return vnode.children
      }

      return ''
    })
    .join('')
})

const dynamicSpread = computed(() => {
  return (textContent.value?.length ?? 0) * props.spread
})

const componentClasses = computed(() =>
  cn(
    'relative inline-block bg-[length:250%_100%,auto] bg-clip-text text-transparent',
    '[background-repeat:no-repeat,padding-box]',
    props.class
  )
)

const componentStyle = computed(
  (): CSSProperties =>
    ({
      '--spread': `${dynamicSpread.value}px`,
      '--bg':
        'linear-gradient(90deg,#0000 calc(50% - var(--spread)),var(--color-background, #fff),#0000 calc(50% + var(--spread)))',
      backgroundImage:
        'var(--bg), linear-gradient(var(--color-muted-foreground, #6b7280), var(--color-muted-foreground, #6b7280))',
    }) as CSSProperties
)

const MotionComponent = computed(() => {
  const motionComponents = motion as Record<string, unknown>

  return motionComponents[props.as] || motion.p
})
</script>

<template>
  <component
    :is="MotionComponent"
    :class="componentClasses"
    :style="componentStyle"
    :initial="{ backgroundPosition: '100% center' }"
    :animate="
      reducedMotion === 'reduce'
        ? { backgroundPosition: '100% center' }
        : { backgroundPosition: '0% center' }
    "
    :transition="{
      repeat: reducedMotion === 'reduce' ? 0 : Number.POSITIVE_INFINITY,
      duration: reducedMotion === 'reduce' ? 0 : duration,
      ease: 'linear',
    }"
  >
    <slot />
  </component>
</template>
